@let r = result();

<!-- Inline markdown parts (bold, italic, code) of a paragraph, list item or
     claim. One line on purpose: whitespace here would show up in the text. -->
<ng-template #inline let-parts>@for (part of parts; track $index) {@if (part.code) {<code>{{ part.text }}</code>} @else if (part.bold) {<strong>{{ part.text }}</strong>} @else if (part.italic) {<em>{{ part.text }}</em>} @else {{{ part.text }}}}</ng-template>

<div class="rv-layout" [class.rv-layout--empty]="!r.sources.length">
  <!-- Side column on a wide panel; above the answer otherwise. -->
  <aside class="rv-rail" aria-label="Summary">
    @if (r.meta.searches.length) {
      <section class="ans-searches" aria-labelledby="ans-searches-title">
        <h3 class="ans-searches__title rv-key" id="ans-searches-title" appFieldTip="meta.searches" [tipText]="tips.searches">
          Searches <span class="rv-seg__count">{{ r.meta.searches.length }}</span>
        </h3>
        <ol class="ans-searches__list">
          @for (s of r.meta.searches; track $index) {
            <li class="ans-search">
              <span class="ans-search__query">{{ s.query }}</span>
              <span class="ans-search__counts">
                @if (s.passagesFound !== null) { {{ s.passagesFound }} found }
                @if (s.passagesFound !== null && s.newPassages !== null) { · }
                @if (s.newPassages !== null) { {{ s.newPassages }} new }
              </span>
            </li>
          }
        </ol>
      </section>
    }

    @if (r.sources.length) {
      <app-top-sites [entries]="r.sources" [selected]="siteFilter()" (toggle)="toggleSite($event)" (clear)="clearSites()" />
    }
  </aside>

  <div class="rv-main">
    @if (r.sources.length) {
      <div class="rv-toolbar">
        <div class="rv-seg" role="tablist" aria-label="Answer or its sources">
          <button type="button" role="tab" class="rv-seg__btn" [class.rv-seg__btn--active]="view() === 'answer'"
                  [attr.aria-selected]="view() === 'answer'" (click)="view.set('answer')">
            Answer
          </button>
          <button type="button" role="tab" class="rv-seg__btn rv-key" [class.rv-seg__btn--active]="view() === 'sources'"
                  [attr.aria-selected]="view() === 'sources'" (click)="view.set('sources')"
                  appFieldTip="sources" [tipText]="tips.sources" [tipDelay]="600">
            Sources <span class="rv-seg__count">{{ r.sources.length }}</span>
          </button>
        </div>
        @if (view() === 'sources') {
          @if (siteFilter().size; as picked) {
            @let n = filteredSources().length;
            <span class="rv-filter" role="status">
              {{ n }} source{{ n === 1 ? '' : 's' }} from
              @if (picked === 1) {
                <strong>{{ siteFilter().values().next().value }}</strong>
              } @else {
                <strong>{{ picked }} sites</strong>
              }
              <button type="button" class="rv-filter__clear" (click)="clearSites()" aria-label="Show sources from all sites">
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true"><path d="M3 3l6 6M9 3l-6 6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>
              </button>
            </span>
          } @else if (citedCount()) {
            <span class="rv-toolbar__hint">{{ citedCount() }} cited, listed first</span>
          }
        }
      </div>
    }

    @if (view() === 'answer' || !r.sources.length) {
    <!-- The answer -->
    <section class="ans-card" aria-label="Answer">
      <div class="ans-card__head">
        @if (r.question) {
          <h3 class="ans-card__question rv-key" appFieldTip="question" [tipText]="tips.question" [tipDelay]="600">{{ r.question }}</h3>
        } @else {
          <span class="ans-card__label rv-key" appFieldTip="answer" [tipText]="tips.answer">Answer</span>
        }
        <span class="ans-card__tags">
          @if (r.meta.mode) {
            <span class="ans-tag rv-key" appFieldTip="meta.mode" [tipText]="tips.mode">{{ r.meta.mode }}</span>
          }
          @if (r.meta.outputType) {
            <span class="ans-tag rv-key" appFieldTip="meta.output_type" [tipText]="tips.outputType">{{ r.meta.outputType }}</span>
          }
          @if (r.structured?.confidence !== null && r.structured?.confidence !== undefined) {
            <span class="rv-score" tabindex="0" appFieldTip="answer.confidence" [tipText]="tips.confidence"><span class="rv-score__dot"></span>Confidence {{ r.structured!.confidence | number:'1.2-2' }}</span>
          }
        </span>
      </div>

      @if (blocks().length) {
        <div class="ans-card__text">
          @for (b of blocks(); track $index) {
            @switch (b.kind) {
              @case ('h') { <p class="ans-card__heading"><ng-container [ngTemplateOutlet]="inline" [ngTemplateOutletContext]="{ $implicit: b.inlines }" /></p> }
              @case ('ul') { <ul>@for (item of b.items; track $index) {<li><ng-container [ngTemplateOutlet]="inline" [ngTemplateOutletContext]="{ $implicit: item }" /></li>}</ul> }
              @case ('ol') { <ol>@for (item of b.items; track $index) {<li><ng-container [ngTemplateOutlet]="inline" [ngTemplateOutletContext]="{ $implicit: item }" /></li>}</ol> }
              @default { <p><ng-container [ngTemplateOutlet]="inline" [ngTemplateOutletContext]="{ $implicit: b.inlines }" /></p> }
            }
          }
        </div>
      } @else if (noAnswerReason(); as reason) {
        <p class="ans-card__none">No answer came back. {{ reason }}</p>
      }

      <!-- Structured: the claims, each followed by Ask Mila-style citation pills -->
      @if (claims().length) {
        <div class="ans-claims">
          <h3 class="ans-claims__title rv-key" appFieldTip="answer.claims" [tipText]="tips.claims">
            Claims <span class="rv-seg__count">{{ claims().length }}</span>
          </h3>
          <ul class="ans-claims__list">
            @for (c of claims(); track $index) {
              <li class="ans-claim">
                <span class="ans-claim__text"><ng-container [ngTemplateOutlet]="inline" [ngTemplateOutletContext]="{ $implicit: c.statement }" /></span>
                @for (ref of c.refs; track ref.url) {
                  {{ ' ' }}<a class="ans-cite" [class.ans-cite--site]="ref.number === null" [href]="ref.url" target="_blank" rel="noopener noreferrer"
                     appFieldTip="source_urls" [tipText]="(ref.title ? ref.title + ' · ' : '') + ref.label"
                     [attr.aria-label]="(ref.number !== null ? 'Source ' + ref.number + ': ' : '') + (ref.title || ref.label)">{{ ref.number ?? ref.label }}</a>
                }
                @if (!c.refs.length) {
                  <span class="ans-claim__unbacked">no retrieved source backs this</span>
                }
              </li>
            }
          </ul>
        </div>
      }
    </section>

    } @else {
      <!-- The articles it was written from -->
      <section class="ans-section" aria-label="Sources">
        <div class="ans-sources-box">
        <ol class="ans-sources">
          @for (s of shownSources(); track s.number) {
            <li>
              <a class="ans-source" [href]="s.source.url" target="_blank" rel="noopener noreferrer">
                @if (showNumbers()) {
                  <span class="ans-cite ans-cite--static" aria-hidden="true">{{ s.number }}</span>
                }
                <span class="ans-source__text">
                  <span class="ans-source__title">{{ s.source.title || s.source.url }}</span>
                  <span class="ans-source__meta">
                    <span class="rv-key" appFieldTip="domain_name" [tipText]="tips.domainName">{{ s.source.host }}</span>
                    @if (s.source.publishedAt) {
                      <span aria-hidden="true">·</span>
                      <time class="rv-key" [attr.datetime]="s.source.publishedAt" appFieldTip="published_at" [tipText]="tips.publishedAt">{{ s.source.publishedAt | date:'mediumDate' }}</time>
                    }
                  </span>
                </span>
              </a>
            </li>
          }
        </ol>

        @if (filteredSources().length > shownSources().length) {
          <button type="button" class="ans-sources-box__more" (click)="showMore()">
            Show more ({{ filteredSources().length - shownSources().length }} left)
          </button>
        }
        </div>
      </section>
    }
  </div>
</div>
